import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Grid,
  Button,
  Autocomplete,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Collapse,
  IconButton,
  Divider,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Description as FileIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroller";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tooltip } from "@mui/material";

// Import your API services
import {
  ApproveIndentlistapi,
  GetApprovedPurchaseOrders,
  exportPurchaseOrdersExcel,
} from "../../../Services/Services";
import { handlePrint } from "../Purchase_order/PurchaseOrderPrint";

function PoIndent() {
  // ===== States =====
  const [indents, setIndents] = useState([]);
  const [selectedIndent, setSelectedIndent] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [indentsLoading, setIndentsLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedItem, setExpandedItem] = useState(null);

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  // ===== Fetch Indents when Division Changes =====
  useEffect(() => {
    if (storedDivision?.division_id) {
      fetchIndentsByDivision(storedDivision.division_id);
    } else {
      setIndents([]);
      setSelectedIndent(null);
      setPurchaseOrders([]);
    }
  }, []);

  // ===== Fetch Purchase Orders when Indent is Selected =====
  useEffect(() => {
    if (storedDivision?.division_id && selectedIndent) {
      setPage(1);
      setHasMore(true);
      setPurchaseOrders([]);
      fetchPurchaseOrders(storedDivision.division_id, selectedIndent, 1, false);
    } else {
      setPurchaseOrders([]);
      setPage(1);
      setHasMore(true);
      setTotalCount(0);
    }
  }, [selectedIndent]);

  const fetchIndentsByDivision = async (divisionId) => {
    if (!divisionId) {
      setIndents([]);
      return;
    }

    setIndentsLoading(true);
    setSelectedIndent(null);
    setPurchaseOrders([]);
    try {
      const filters = {
        division: divisionId,
        indent_no: "",
      };
      const data = await ApproveIndentlistapi(1, filters);
      const indentsList = data.results || data || [];
      setIndents(Array.isArray(indentsList) ? indentsList : []);

      if (indentsList.length === 0) {
        toast.info("No indents found for this division");
      }
    } catch (error) {
      console.error("Error fetching indents:", error);
      toast.error("Failed to load indents");
    } finally {
      setIndentsLoading(false);
    }
  };

  const fetchPurchaseOrders = async (
    divisionId,
    indent,
    pageNum = 1,
    append = false,
  ) => {
    if (pageNum === 1) {
      setLoading(true);
    }

    try {
      const filters = {
        division: divisionId,
        indent_id: indent.id,
        indent_no: indent.indent_no || indent.indent_number,
        page: pageNum,
      };
      const data = await GetApprovedPurchaseOrders(filters);
      const poList = data.results || data || [];
      const count = data.count || poList.length;

      if (append) {
        setPurchaseOrders((prev) => [...prev, ...poList]);
      } else {
        setPurchaseOrders(Array.isArray(poList) ? poList : []);
      }

      setTotalCount(count);
      setHasMore(data.next !== null);
      setPage(pageNum);

      if (pageNum === 1 && poList.length === 0) {
        toast.info("No purchase orders found for this indent");
      } else if (pageNum === 1) {
        toast.success(`Found ${count} purchase order(s)`);
      }
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      toast.error("Failed to load purchase orders");
      if (!append) {
        setPurchaseOrders([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreData = () => {
    if (storedDivision?.division_id && selectedIndent && hasMore) {
      fetchPurchaseOrders(
        storedDivision.division_id,
        selectedIndent,
        page + 1,
        true,
      );
    }
  };

  const handleReset = () => {
    setSelectedIndent(null);
    setPurchaseOrders([]);
    setIndents([]);
    setPage(1);
    setHasMore(true);
    setTotalCount(0);
  };

  const handleExcelExport = async () => {
    if (!storedDivision?.division_id) {
      toast.warning("Please select a division from sidebar");
      return;
    }

    if (!selectedIndent) {
      toast.warning("Please select an Indent No first");
      return;
    }

    setExcelLoading(true);
    try {
      const filters = {
        division: storedDivision.division_id,
        indent_id: selectedIndent.id,
        indent_no: selectedIndent.indent_no || selectedIndent.indent_number,
      };

      const blob = await exportPurchaseOrdersExcel(filters);

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Purchase_Orders_${selectedIndent.indent_no || "export"}_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel downloaded successfully!");
      } else {
        toast.error("Failed to export Excel");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("approved") || statusLower.includes("complete")) {
      return "#4caf50";
    } else if (statusLower.includes("pending")) {
      return "#ff9800";
    } else if (
      statusLower.includes("rejected") ||
      statusLower.includes("cancel")
    ) {
      return "#f44336";
    } else if (
      statusLower.includes("partial") ||
      statusLower.includes("draft")
    ) {
      return "#2196f3";
    }
    return "#9e9e9e";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const datePart = dateString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Calculate totals
  const totalPOs = purchaseOrders.length;

  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3000} />

      <Box sx={{ mb: 2, mt: -4 }}>
        <Grid container alignItems="center">
          <Grid item xs={12} sm={6} md={9}>
            <Autocomplete
              options={indents}
              getOptionLabel={(option) =>
                option.indent_no || option.indent_number || ""
              }
              value={selectedIndent}
              onChange={(event, newValue) => {
                setSelectedIndent(newValue);
              }}
              loading={indentsLoading}
              disabled={!storedDivision?.division_id} 
              size="small"
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography sx={{ fontSize: "0.85rem" }}>
                      {option.indent_no || option.indent_number}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                    >
                      Date: {formatDate(option.created_at || option.date)}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Indent No"
                  placeholder={
                    storedDivision?.division_id
                      ? "Select Indent No"
                      : "Select Division from sidebar first"
                  }
                  sx={{
                    "& .MuiInputBase-root": {
                      height: 35,
                      borderRadius: 0,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.7rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {indentsLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
            />
          </Grid>

          {/* Spacer - pushes buttons to right */}
          <Grid
            item
            xs={0}
            sm={0}
            md={1}
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Reset Button */}
          <Grid item xs={6} sm={3} md={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: "32px",
                whiteSpace: "nowrap",
                borderRadius: "0",
                fontSize: "0.7rem",
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>

          {/* Excel Export Button */}
          <Grid item xs={6} sm={3} md={1}>
            <Tooltip
              title={excelLoading ? "Exporting..." : "Download Excel"}
              arrow
            >
              <span>
                <IconButton
                  color="success"
                  size="small"
                  onClick={handleExcelExport}
                  disabled={excelLoading || !selectedIndent}
                  sx={{
                    borderRadius: "4px",
                    padding: "6px",
                  }}
                >
                  {excelLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <FileIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No Data Message */}
      {!loading && purchaseOrders.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: "#fafafa",
            borderRadius: 2,
            border: "1px dashed #ccc",
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {!storedDivision
              ? "📋 Please select a Division"
              : !selectedIndent
                ? "📝 Please select an Indent No"
                : "📭 No Purchase Orders found"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {!storedDivision
              ? "Choose a division from the dropdown to view available indents"
              : !selectedIndent
                ? "Choose an indent number to view the purchase orders"
                : "No purchase orders found for the selected indent"}
          </Typography>
        </Box>
      )}

      {/* Data Display - Mobile vs Desktop */}
      {!loading && purchaseOrders.length > 0 && (
        <>
          {isMobile ? (
            /* 📱 MOBILE VIEW */
            <Box
              id="scrollableMobileContainer"
              sx={{
                bgcolor: "background.paper",
                maxHeight: "65vh",
                overflowY: "auto",
              }}
            >
              <InfiniteScroll
                dataLength={purchaseOrders.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      Loading more...
                    </Typography>
                  </Box>
                }
                endMessage={
                  purchaseOrders.length > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 1 }}
                    >
                      No more purchase orders
                    </Typography>
                  )
                }
                scrollableTarget="scrollableMobileContainer"
              >
                {purchaseOrders.map((po, index) => (
                  <Box key={po.po_id || index}>
                    {/* Header Card */}
                    <Box
                      onClick={() => {
                        setExpandedItem(expandedItem === index ? null : index);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        cursor: "pointer",
                        bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:active": { bgcolor: "#e3f2fd" },
                        borderLeft: `4px solid ${getStatusColor(po.approval_status)}`,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#666",
                              minWidth: 25,
                            }}
                          >
                            #{index + 1}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.85rem",
                              flex: 1,
                              color: "#1976d2",
                            }}
                          >
                            {po.po_number || po.po_no || "-"}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#666",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>
                            {po.supplier_name || "-"}
                          </span>
                          <span style={{ color: "#999" }}>•</span>
                          <span style={{ fontSize: "0.7rem" }}>
                            {formatDate(po.created_at)}
                          </span>
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrint(po);
                          }}
                        >
                          <PrintIcon sx={{ fontSize: "1.2rem" }} />
                        </IconButton>
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          {expandedItem === index ? (
                            <ExpandLess fontSize="small" />
                          ) : (
                            <ExpandMore fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </Box>

                    <Collapse
                      in={expandedItem === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1.5,
                          bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                          borderLeft: `4px solid ${getStatusColor(po.approval_status)}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1.2,
                          }}
                        >
                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              PO NUMBER
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                color: "#1976d2",
                              }}
                            >
                              {po.po_number || po.po_no || "-"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              STATUS
                            </Typography>
                            <Box
                              sx={{
                                bgcolor: getStatusColor(po.approval_status),
                                color: "#fff",
                                borderRadius: "12px",
                                px: 1,
                                py: 0.2,
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                display: "inline-block",
                              }}
                            >
                              {po.approval_status || "N/A"}
                            </Box>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              CREATED AT
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {new Date(po.created_at).toLocaleDateString(
                                "en-GB",
                              )}
                            </Typography>
                          </Box>

                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              SUPPLIER
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                color: "#333",
                              }}
                            >
                              {po.supplier_name || "-"}
                            </Typography>
                          </Box>

                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              BILLING ADDRESS
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {po.billing_address || "-"}
                            </Typography>
                          </Box>

                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              SHIPPING ADDRESS
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {po.shipping_address || "-"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              APPROVED BY
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: "#4caf50",
                              }}
                            >
                              {po.approved_name || "-"}
                            </Typography>
                          </Box>

                          {(po.remarks || po.remark) && (
                            <Box sx={{ gridColumn: "1 / -1" }}>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                REMARKS
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 500,
                                  color: "#555",
                                  fontStyle: "italic",
                                }}
                              >
                                {po.remarks || po.remark}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Collapse>
                    <Divider />
                  </Box>
                ))}
              </InfiniteScroll>

              {/* Mobile Total Footer */}
              <Box
                sx={{
                  p: 0.5,
                  bgcolor: "#e8f5e9",
                  borderTop: "2px solid #4caf50",
                  position: "sticky",
                  bottom: 0,
                }}
              ></Box>
            </Box>
          ) : (
            /* 💻 DESKTOP VIEW */
            <TableContainer
              component={Paper}
              id="scrollableTableContainer"
              sx={{
                maxHeight: "65vh",
                overflowX: { xs: "auto", sm: "unset" },
                overflowY: "auto",
                width: "100%",
              }}
            >
              <InfiniteScroll
                dataLength={purchaseOrders.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      Loading more...
                    </Typography>
                  </Box>
                }
                endMessage={
                  purchaseOrders.length > 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 1 }}
                    >
                      No more purchase orders
                    </Typography>
                  )
                }
                scrollableTarget="scrollableTableContainer"
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: "max-content",
                    "& td, & th": {
                      borderBottom: "1px solid #ccc",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableHead
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        margin: 0,
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "#2196f3" : "#333",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#f5f5f5",
                          zIndex: 3,
                          minWidth: 50,
                          padding: "4px 8px",
                        }}
                      >
                        S. No.
                      </TableCell>
                      <TableCell>PO Number</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Billing Address</TableCell>
                      <TableCell>Shipping Address</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Approved By</TableCell>
                      <TableCell>Remarks</TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#f5f5f5 !important",
                          zIndex: 3,
                          minWidth: 80,
                          borderLeft: "2px solid #e0e0e0",
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {purchaseOrders.map((po, index) => (
                      <TableRow
                        hover
                        key={po.po_id || index}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#fafafa" : "#ffffff",
                          "&:hover": {
                            backgroundColor: "#e3f2fd",
                            "& td": { backgroundColor: "#e3f2fd !important" },
                          },
                          "& td": {
                            paddingRight: "13px !important",
                            paddingLeft: "13px !important",
                            padding: "0px",
                            fontSize: { xs: "0.65rem", sm: "0.8rem" },
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      >
                        <TableCell
                          align="center"
                          sx={{
                            position: "sticky",
                            left: 0,
                            backgroundColor:
                              index % 2 === 0
                                ? "#fafafa !important"
                                : "#ffffff !important",
                            zIndex: 2,
                            minWidth: 50,
                          }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#1976d2" }}>
                          {po.po_number || po.po_no || "-"}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              bgcolor: getStatusColor(po.approval_status),
                              color: "#fff",
                              borderRadius: "12px",
                              px: 1.5,
                              py: 0.3,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              display: "inline-block",
                            }}
                          >
                            {po.approval_status || "N/A"}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {po.supplier_name || "-"}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Tooltip title={po.billing_address || "-"} arrow>
                            <span>{po.billing_address || "-"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Tooltip title={po.shipping_address || "-"} arrow>
                            <span>{po.shipping_address || "-"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {new Date(po.created_at).toLocaleDateString("en-GB")}
                        </TableCell>
                        <TableCell sx={{ color: "#4caf50", fontWeight: 500 }}>
                          {po.approved_name || "-"}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <Tooltip title={po.remarks || po.remark || "-"} arrow>
                            <span>{po.remarks || po.remark || "-"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            backgroundColor:
                              index % 2 === 0
                                ? "#fafafa !important"
                                : "#ffffff !important",
                            zIndex: 2,
                            borderLeft: "2px solid #e0e0e0",
                          }}
                        >
                          <Tooltip title="Print PO" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handlePrint(po)}
                              sx={{ "&:hover": { backgroundColor: "#e3f2fd" } }}
                            >
                              <PrintIcon sx={{ fontSize: "1.1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </InfiniteScroll>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

export default PoIndent;
